import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { setAuthHeader } from '@/api/client'
import type { AuthResponse } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const user = ref<{ id: number; username: string; role: string } | null>(null)

  const isAuthenticated = computed(() => !!accessToken.value)

  async function login(email: string, password: string) {
    const data = await authApi.login(email, password)
    applyAuth(data)
  }

  async function register(email: string, username: string, password: string) {
    const data = await authApi.register(email, username, password)
    applyAuth(data)
  }

  async function tryRefresh(): Promise<boolean> {
    try {
      const data = await authApi.refresh()
      applyAuth(data)
      return true
    } catch {
      clearAuth()
      return false
    }
  }

  async function logout() {
    try { await authApi.logout() } catch { /* ignore network errors */ }
    clearAuth()
  }

  function applyAuth(data: AuthResponse) {
    accessToken.value = data.accessToken
    user.value = { id: data.userId, username: data.username, role: data.role }
    setAuthHeader(data.accessToken)
  }

  function clearAuth() {
    accessToken.value = null
    user.value = null
    setAuthHeader(null)
  }

  return { accessToken, user, isAuthenticated, login, register, tryRefresh, logout }
})
